import { Component, OnInit, ViewChild, ChangeDetectorRef, Input, OnChanges } from '@angular/core';
import { DataBindingDirective } from '@progress/kendo-angular-grid';
import { SVGIcon, fileExcelIcon, pencilIcon, trashIcon, downloadIcon } from '@progress/kendo-svg-icons';
import { process } from '@progress/kendo-data-query';
import {
    SpreadsheetModule,
    SheetDescriptor,
    SpreadsheetMainMenuItem,
    SpreadsheetChangeEvent,
    SpreadsheetActiveSheetChangeEvent,
    SpreadsheetExcelExportEvent,
    SpreadsheetExcelImportEvent,
    SpreadsheetComponent
} from '@progress/kendo-angular-spreadsheet';
import { ChartsModule } from '@progress/kendo-angular-charts';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { ExcelExportModule, ExcelExportComponent } from "@progress/kendo-angular-excel-export";
import { ButtonsModule } from "@progress/kendo-angular-buttons";
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { GridService } from 'app/services/grid.service';
import { SharedService } from 'app/services/shared.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Observable, catchError, concatMap, forkJoin, from, of } from 'rxjs';
import { Subject } from 'rxjs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MenuModel } from 'app/models/menu.model';
import { PageService } from 'app/services/page.service';
import { DrakeSharedService } from 'app/services/drake-shared.service';
import { TranslocoPipe } from '@ngneat/transloco';
import tinycolor from 'tinycolor2';

@Component({
    selector: 'app-drake-quickedit',
    standalone: true,
    imports: [
        CommonModule,
        HttpClientModule,
        SpreadsheetModule,
        ChartsModule,
        InputsModule,
        ButtonsModule,
        ExcelExportModule,
        MatToolbarModule,
        MatDialogModule
    ],
    templateUrl: './drake-quickedit.component.html',
    styleUrls: ['./drake-quickedit.component.scss'],
    providers: [TranslocoPipe]
})
export class DrakeQuickeditComponent implements OnInit, OnChanges {
    @ViewChild(DataBindingDirective) dataBinding: DataBindingDirective;
    @ViewChild(ExcelExportComponent) excelExportComponent: ExcelExportComponent; // Reference to the Excel Export component
    @ViewChild(SpreadsheetComponent) spreadsheet: SpreadsheetComponent;

    public gridData: any[] = [];
    public sheets: SheetDescriptor[] = [];
    public gridColumns: any[] = [];
    public items: SpreadsheetMainMenuItem[] = [
        { id: "file", active: true },
    ];

    public selectedRow: number | null = null;
    public activeSheet: any;


    public canEdit: boolean = false;
    public canDelete: boolean = false;
    public canExport: boolean = false;

    @Input() public viewCode: string = '';
    @Input() public filter: string = '{}';
    @Input() public sort: string = '{}';
    @Input() public commandName: string = 'READ';
    @Input() public scope: string = '';

    public height = window.innerHeight - 70;
    private customColor: string = "#eee"
    public rowNumber = 1;
    public colNumber = 1;
    public statusError: string;
    public isHandlingChange: boolean = false;
    public hasError: boolean = false;

    constructor(
        private gridService: GridService,
        public dialog: MatDialog,
        private router: Router,
        private drakeShared: DrakeSharedService,
        private translocoPipe: TranslocoPipe) { }

    ngOnInit(): void {
        const branding = JSON.parse(localStorage.getItem('branding'));
        if (branding)
            this.customColor = this.lighten(branding.brdColor3, 40);

        this.drakeShared.addRowSubject.subscribe(() => this.addRow());
        this.drakeShared.removeRowSubject.subscribe(() => this.removeRow());
        this.drakeShared.saveQuickEditSubject.subscribe(() => {
            this.saveQuickEdit();
        });
    }

    lighten(color: string, percent: number): string {
        return tinycolor(color).lighten(percent).toString();
    }

    loadGridConfiguration(): void {
        if (!this.viewCode || !this.scope || !this.commandName) {
            return; // Ensure that all necessary data is available before proceeding
        }

        // Carica la configurazione della griglia
        this.gridService.getScopeView(this.viewCode).subscribe(
            (viewConfig: any) => {
                this.canEdit = viewConfig.canEdit;
                this.canDelete = viewConfig.canDelete;
                this.canExport = viewConfig.canExport;
                this.gridColumns = viewConfig.fields.map((field: any) => {
                    const columnConfig = {
                        code: field.fieldCode,
                        field: field.columnName,
                        title: field.header,
                        width: field.size,
                        hidden: !field.visible,
                        filter: field.filter,
                        sortable: field.sort,
                        format: field.format ? `{0:${field.format}}` : undefined,
                        cssClass: field.cssClass,
                        template: field.template,
                        fieldType: field.fieldType,
                        fieldControlType: field.fieldControlType,
                        fieldControlFormat: field.fieldControlFormat,
                        order: field.order,
                        group: field.group,
                        required: field.required,
                        enable: !(field.fieldType == 'FILE' || field.columnName == 'DomCode'),

                    };
                    this.colNumber = this.gridColumns.length + 2;
                    return columnConfig;
                });
                this.loadGridData();
            },
            (error) => {
                console.error('Errore durante il caricamento della configurazione della griglia', error);
            }
        );
    }

    loadGridData(): void {
        this.gridColumns.forEach(col => {
            if (col.fieldControlType == "DROPDOWNLIST") {
                this.gridService.getDropDownValues(col.code).subscribe((result: any) => {
                    col.options = result;
                });
            }
        });

        this.sheets = [];
        const requestData = {
            ViewCode: this.viewCode,
            Filter: this.filter,
            Sort: this.sort,
            Page: 0,
            PageSize: 0,
            Language: "",
            Cache: false,
            ExtraData: {}
        };
        console.log('requestData: ', requestData);
        this.gridService.getScopeData(this.scope, this.commandName, requestData).subscribe(
            (response: any) => {
                this.gridData = response.data.map((item: any) => {
                    for (let key in item) {
                        if (item[key] === null || item[key] === undefined) {
                            item[key] = '';
                        }
                    }
                    return item;
                });

                this.rowNumber = this.gridData.length + 1;

                this.sheets = [{
                    name: this.scope,
                    rows: [
                        {
                            cells: [
                                {
                                    value: "ID",
                                    background: this.customColor,
                                    bold: true,
                                    enable: false
                                },
                                {
                                    value: "STATUS",
                                    background: this.customColor,
                                    bold: true,
                                    enable: false
                                },
                                ...this.gridColumns.map(c => {
                                    return {
                                        value: this.translocoPipe.transform(c.title),
                                        background: this.customColor,
                                        bold: true,
                                        enable: false
                                    }
                                })
                            ]
                        },
                        ...this.gridData.map(d => {
                            var rowItem = [];
                            rowItem.push({ value: d["_id"], enable: false });
                            rowItem.push({ value: "", enable: false });

                            this.gridColumns.forEach(c => {
                                var cell = { value: d[c.field], enable: d._editable };
                                // Controllo per mostrare il nome del file se il valore è un oggetto
                                if (!c.enable && typeof cell.value === 'object' && cell.value !== null) {
                                    cell.value = cell.value.fileName || cell.value.text; // Mostra il nome del file o converti l'oggetto a stringa
                                }

                                // Aggiungere un controllo di sicurezza per verificare se cell.value è definito e ha la proprietà text
                                if (cell.value && typeof cell.value === 'object' && 'text' in cell.value) {
                                    cell.value = cell.value.text;
                                }
                                rowItem.push(cell);
                            });


                            return { cells: rowItem };
                        })
                    ],
                    columns: this.gridColumns.map(c => { return { width: c.width } }),
                    frozenRows: 1,
                    frozenColumns: 2
                },
                ]
                console.log(this.sheets);
            },
            (error) => {
                console.error('Error loading grid data', error);
                console.error('Request Data:', requestData);
            }
        );
    }

    editActivity(dataItem: any): void {
        this.router.navigate(['/d', this.scope, 'f', dataItem._id], );
    }

    ngOnChanges(changes) {
        console.log("qechanges", changes);
        if ((changes.scope && changes.scope.currentValue != '') || (changes.viewCode && changes.viewCode.currentValue != '')){
            this.loadGridConfiguration();
            this.drakeShared.addBreadcrumbUrl();
        }
    }

    exportToExcel(): void {
        this.excelExportComponent.save();
    }

    private updateCellValue(sheet: any, row: number, column: number, value: string) {
        sheet.range(row, column, 1, 1).value(value);
        this.sheets[0].rows[row].cells[column].value = value;
    }

    public onChange(event: SpreadsheetChangeEvent, type: string): void {
        if (this.isHandlingChange) {
            return;
        }
        this.isHandlingChange = true;
        var colLength = this.gridColumns.length + 2;
        var gridCols = this.gridColumns;

        var range = event.range as any;

        var colIndex = range._ref.topLeft.col - 2;
        console.log('range:', range);
        console.log('range.column:', range._ref.topLeft.col);
        console.log('gridCols:', gridCols);
        console.log('colIndex:', colIndex);

        let uniqueRows = new Set<number>();
        event.range.forEachCell((row, column, cellProperties) => {
            uniqueRows.add(row);
            this.sheets[0].rows[row].cells[column].value = cellProperties.value;
        });

        let sheet: any = this.spreadsheet.spreadsheetWidget.activeSheet();
        uniqueRows.forEach(row => {
            let errorCells = this.validateRow(sheet, row, gridCols);
            let rowHasError = errorCells.length > 0;
            if (rowHasError) {
                this.updateCellValue(sheet, row, 1, 'ERROR');
                sheet.range(row, 1, 1, 1).value('ERROR');
                sheet.range(row, 0, 1, colLength).background('#ffb3b3'); // Rosso chiaro
                errorCells.forEach(cell => {
                    sheet.range(row, cell, 1, 1).background('#fa5050'); // Rosso scuro
                });
            } else {
                this.updateCellValue(sheet, row, 1, 'EDIT');
                sheet.range(row, 1, 1, 1).value('EDIT');
                sheet.range(row, 0, 1, colLength).background('#ffffbb'); // Giallo
            }
        });

        this.isHandlingChange = false;
    }

    private validateRow(sheet: any, row: number, gridCols: any[]): number[] {
        let errorCells: number[] = [];

        const status = sheet.range(row, 1, 1, 1).value();
        if (status === 'REMOVED') {
            return errorCells;
        }

        gridCols.forEach((colCfg, index) => {
            const cellValue = sheet.range(row, index + 2, 1, 1).value();
            console.log(`Row: ${row}, Column: ${index + 2}, Cell Value: ${cellValue}`);

            if (colCfg.required && (cellValue === null || cellValue === undefined || cellValue === '')) {
                errorCells.push(index + 2);
            } else {
                switch (colCfg.fieldControlType) {
                    case "DROPDOWNLIST":
                        if (colCfg.options && cellValue !== null && cellValue !== "") {
                            const isValid = colCfg.options.some(option => option.text === cellValue);
                            if (!isValid) {
                                errorCells.push(index + 2);
                            }
                        }
                        break;
                }

                switch (colCfg.fieldType) {
                    case "INT":
                        if (cellValue !== null && cellValue !== "") {
                            const isValid = Number.isInteger(Number(cellValue));
                            if (!isValid) {
                                errorCells.push(index + 2);
                            }
                        }
                        break;

                    case "DECIMAL":
                        if (cellValue !== null && cellValue !== "") {
                            const isValid = !isNaN(parseFloat(String(cellValue))) && parseFloat(String(cellValue)).toString() === cellValue.toString();
                            if (!isValid) {
                                errorCells.push(index + 2);
                            }
                        }
                        break;

                    case "BOOL":
                        if (cellValue !== null && cellValue !== "") {
                            const isValid = String(cellValue).toLowerCase() === 'true' || String(cellValue).toLowerCase() === 'false';
                            if (!isValid) {
                                errorCells.push(index + 2);
                            }
                        }
                        break;

                    case "DATETIME":
                        if (cellValue !== null && cellValue !== "") {
                            const isValid = !isNaN(Date.parse(String(cellValue)));
                            if (!isValid) {
                                errorCells.push(index + 2);
                            }
                        }
                        break;
                }
            }
        });

        return errorCells;
    }

    private createRow(dataItem: any = {}): any {
        let rowItem = [];
        rowItem.push({ value: dataItem["_id"] || '', enable: false });
        rowItem.push({ value: dataItem["status"] || '', enable: false });

        this.gridColumns.forEach(c => {
            var cell = { value: dataItem[c.field] || 'pippo', enable: c.enable };
            if (!c.enable && typeof cell.value === 'object' && cell.value !== null) {
                cell.value = cell.value.fileName || cell.value.text;
            }

            if (cell.value && typeof cell.value === 'object' && 'text' in cell.value) {
                cell.value = cell.value.text;
            }
            rowItem.push(cell);
        });
        console.log(rowItem);
        return { cells: rowItem };
    }


    public addRow() {

        this.rowNumber += 1;
        const sheet = this.sheets[0];
        const newRow = this.createRow();
        sheet.rows.push(newRow);

        (this.spreadsheet.spreadsheetWidget.activeSheet() as any).insertRow(this.rowNumber - 1, false);

        /*const newRowIdx = sheet.rows.length - 1;
        console.log("New row index:", newRowIdx);

        let activeSheet: any = (this.spreadsheet.spreadsheetWidget as any).sheetByName(this.spreadsheet.activeSheet);

        return;
        activeSheet.range(newRowIdx, 0, 1, 1).enable(false);
        activeSheet.range(newRowIdx, 1, 1, 1).enable(false);
  
        console.log("Disabled cells in new row:", newRowIdx);*/

    }


    public removeRow() {
        this.isHandlingChange = true;
        try {
            if(!this.gridData[this.selectedRow - 1]._editable){
                var notification = this.translocoPipe.transform("notification.error.quickedit.noPermissionRemove");
                this.drakeShared.showTemporaryNotification(notification, "info")
                return;
            }
            let sheet: any = (this.spreadsheet?.spreadsheetWidget.activeSheet() as any);

            if (sheet && this.selectedRow !== null && this.selectedRow > 0) {
                let colLength = this.gridColumns.length + 2;
                for (let col = 0; col < this.colNumber; col++) {

                    sheet.range(this.selectedRow, 1, 1, 1).value('REMOVED');
                    this.sheets[0].rows[this.selectedRow].cells[1].value = 'REMOVED';

                    sheet.range(this.selectedRow, col, 1, colLength).background('#e5b4f7'); 
                    sheet.range(this.selectedRow, col, 1, colLength).enable(false);
                }
                sheet.range(this.selectedRow, 1, 1, 1).value('REMOVED');

                const statusCell = sheet.range(this.selectedRow, 1, 1, 1);
                statusCell.value('REMOVED');

                this.gridData[this.selectedRow - 1]["STATUS"] = "REMOVED";

                console.log('Updated status to REMOVED for row', this.selectedRow);
            }
        }
        catch(ex){

            console.log(ex)
        }

        this.isHandlingChange = false;
    }



    private saveQuickEdit() {
        const sheet = this.sheets[0];
        const activeSheet = (this.spreadsheet.spreadsheetWidget.activeSheet() as any);
        const rowsToSave = [];
        const rowsToRemove = [];

        sheet.rows.forEach((row, rowIndex) => {
            const statusCell = row.cells[1];
            const idCell = row.cells[0];

            if (statusCell && statusCell.value === 'EDIT') {
                rowsToSave.push({ row, rowIndex, idCell });
            } else if (statusCell && statusCell.value === 'REMOVED' && idCell && idCell.value) {
                rowsToRemove.push({ row, rowIndex, idCell });
            }
        });

        const saveObservables = rowsToSave.map(({ row, rowIndex, idCell }) => {
            const rowData: any = {};
            row.cells.forEach((cell: any, index: number) => {
                const colCfg = this.gridColumns[index - 2];
                if (colCfg) {
                    let cellValue = cell.value;
                    if (cellValue === '') {
                        cellValue = null;
                    }
                    if (colCfg.fieldControlType === "DROPDOWNLIST" && colCfg.options) {
                        const option = colCfg.options.find((opt: any) => opt.text === cellValue);
                        if (option) {
                            cellValue = option.value;
                        }
                    }
                    rowData[colCfg.field] = cellValue;
                }
            });

            if (idCell.value === '') {
                rowData['_id'] = this.generateUniqueId();
            } else {
                rowData['_id'] = idCell.value;
            }

            console.log('Saving data:', rowData);

            return this.gridService.saveScopeData(this.scope, "UPSERT", rowData, false).pipe(
                concatMap((result: any) => {
                    if (idCell.value === '') {
                        idCell.value = result.data[0]._id;
                        activeSheet.range(rowIndex, 0, 1, 1).value(idCell.value);
                    }
                    var notification = this.translocoPipe.transform("notification.success.quickedit.save");
                    this.drakeShared.showTemporaryNotification(notification, 'success');
                    activeSheet.range(rowIndex, 0, 1, this.gridColumns.length + 2).background('#93faac'); // Verde
                    return of(result);
                }),
                catchError(err => {
                    console.error('Error during save:', err);
                    var notification = this.translocoPipe.transform("notification.error.quickedit.save");
                    this.drakeShared.showTemporaryNotification(notification, 'error');
                    activeSheet.range(rowIndex, 0, 1, this.gridColumns.length + 2).background('#f7a8b0'); // Rosso
                    return of(null);
                })
            );
        });

        const removeObservables = rowsToRemove.map(({ row, rowIndex, idCell }) => {
            if (idCell && idCell.value) {
                return this.gridService.deleteScopeData(this.scope, "DELETE", { _id: idCell.value }).pipe(
                    concatMap((result: any) => {
                        var notification = this.translocoPipe.transform("notification.success.quickedit.removeRow");
                        this.drakeShared.showTemporaryNotification(notification, 'success');
                        activeSheet.range(rowIndex, 0, 1, this.gridColumns.length + 2).background('#d4edda'); // Verde
                        return of(result);
                    }),
                    catchError(err => {
                        console.error('Error during remove:', err);
                        var notification = this.translocoPipe.transform("notification.error.quickedit.removeRow");
                        this.drakeShared.showTemporaryNotification(notification, 'error');
                        activeSheet.range(rowIndex, 0, 1, this.gridColumns.length + 2).background('#f8d7da'); // Rosso
                        return of(null);
                    })
                );
            }
        }).filter(Boolean);

        from([...saveObservables, ...removeObservables])
            .pipe(concatMap(obs => obs))
            .subscribe({
                complete: () => {
                    console.log('Tutti i salvataggi e le eliminazioni sono stati completati.');
                    this.loadGridData();
                }
            });
    }


    public generateUniqueId() {
        return Math.random().toString(36).substr(2, 9);
    }




    public onSelectionChange(event: any): void {
        console.log("Event: ", event);

        if (event.range) {
            const range = event.range;
            console.log("Range: ", range);

            this.activeSheet = event.sender.activeSheet();
            if (range._ref && range._ref.row !== undefined) {
                this.selectedRow = range._ref.row;
                console.log("Selected row: ", this.selectedRow);
            } else {
                console.error("row is undefined in _ref");
            }
        } else {
            console.error("range is undefined");
        }
    }






    private getRowByIndex(i: number) {
        i--;
        if (!this.gridData[i])
            this.gridData[i] = {};

        return this.gridData[i];
    }

    private getColByIndex(i: number) {
        i = i - 2;
        return this.gridColumns[i];
    }

    getColumnName(index: number) {
        index -= 1; //adjust so it matches 0-indexed array rather than 1-indexed column

        var quotient = index / 26;
        if (quotient > 0)
            return this.getColumnName(quotient) + String.fromCharCode(64 + index % 26);
        else
            return String.fromCharCode(64 + index % 26);
    }
}
